import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FC } from 'react';
import { MdAttachFile } from 'react-icons/md';

import { fileNameFromUrl, hasItemContent, toPlainText, type TFeedbackItem } from './feedback-answer-text';

const BRAND_DEEP = '#c83a55';

interface AnswerListProps {
  items: TFeedbackItem[];
  /** Number the prompts — useful for a long submission the teacher works through. */
  numbered?: boolean;
  /** Border colour of the grades table; matches the surface the list sits on. */
  gradeBorderColor?: string;
  /** Colour of the prompts, grades and file links. */
  accentColor?: string;
}

/**
 * Answers as readable text: each prompt a small heading over the answer, grades
 * as one row per topic, uploads as links. Used for the student's answers on the
 * teacher's review page and for the teacher's feedback on the student's page.
 */
export const AnswerList: FC<AnswerListProps> = ({
  items,
  numbered = false,
  gradeBorderColor = '#ffe1e8',
  accentColor = BRAND_DEEP,
}) => (
  <Stack spacing={2.5}>
    {items.map(({ question, lines, grades, fileUrls }, index) => (
      <Box key={question.id} sx={{ display: 'flex', gap: 1.5 }}>
        {numbered && (
          <Typography
            variant="body2"
            sx={{ color: '#ff4d6d', fontWeight: 700, fontSize: '0.8125rem', minWidth: 18, pt: '1px' }}
          >
            {index + 1}
          </Typography>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* body2: the theme enlarges body1 on phones, which would put the prompt above its answer in size. */}
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8125rem', fontWeight: 700, color: accentColor, mb: 0.5, lineHeight: 1.4 }}
          >
            {toPlainText(question.questionText)}
          </Typography>

          {!hasItemContent({ question, lines, grades, fileUrls }) && (
            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No answer
            </Typography>
          )}

          {lines.length > 0 && (
            <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
              {lines.join('\n')}
            </Typography>
          )}

          {grades.length > 0 && (
            <Box
              sx={{
                mt: lines.length > 0 ? 1 : 0,
                bgcolor: 'common.white',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: gradeBorderColor,
              }}
            >
              {grades.map(grade => (
                <Box
                  key={grade.rowId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 1.5,
                    py: 0.75,
                    '& + &': { borderTop: '1px solid', borderColor: gradeBorderColor },
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {grade.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: accentColor,
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {grade.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {fileUrls.map(url => (
            <Link
              key={url}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, mr: 2, color: accentColor }}
            >
              <MdAttachFile size={16} />
              {fileNameFromUrl(url)}
            </Link>
          ))}
        </Box>
      </Box>
    ))}
  </Stack>
);
